import { HELP_COVERAGE } from './helpCoverage'
import { supabase } from './supabase'

/**
 * THE PAGE YOU ARE ON, AND WHAT TO ASK ABOUT IT (31 Oct, HLP-44; v2 S2).
 *
 * The Ask One panel shows a chip naming the page and, when nothing has been
 * asked yet, three starter questions about it. Both come from data the app
 * already has, so neither is a second list to keep in step:
 *
 *  - the PAGE is the route, matched against `HELP_COVERAGE` - the map the suite
 *    already forces to name every page in the navigation (155). A page with no
 *    entry there has no chip, rather than a chip that says nothing.
 *  - the STARTERS are the corpus QUESTIONS of the entries that map lists for
 *    the page, read from `help_corpus(locale)` - the same rows One answers
 *    from, in the reader's language with 084's per-entry fallback. Nothing is
 *    retyped here, so a starter can never ask about something the corpus does
 *    not hold (the suite already proves every key in the map is loaded).
 *
 * WHAT THE CHIP DOES, SAID PLAINLY: it chooses the starters, and since S4
 * (ask-one v10, 31 Oct) its route and name go with the question AS WORDS -
 * context for a short question, never a permission or a filter. Removing the
 * chip stops both. askone_what says the same (340b), so the chip claims no
 * reach it does not have (rule 4).
 */

/** The HELP_COVERAGE key for a router path: the longest key that is the path
 *  or a prefix of it at a segment boundary. `/` is the hub (''). Null = none. */
export function pageKeyFor(pathname: string): string | null {
  const path = pathname.replace(/^\/+|\/+$/g, '')
  if (path === '') return ''
  let best: string | null = null
  for (const key of Object.keys(HELP_COVERAGE)) {
    if (key === '') continue
    if (path === key || path.startsWith(key + '/')) {
      if (best === null || key.length > best.length) best = key
    }
  }
  return best
}

/** Up to `n` starter questions for a page, in the map's order. A key the
 *  corpus did not return is skipped, never invented. Null page → the hub's. */
export function startersFor(
  pageKey: string | null,
  questions: ReadonlyMap<string, string>,
  n = 3,
): string[] {
  const keys = HELP_COVERAGE[pageKey ?? ''] ?? HELP_COVERAGE['']
  const out: string[] = []
  for (const k of keys) {
    const q = questions.get(k)?.trim()
    if (q && !out.includes(q)) out.push(q)
    if (out.length >= n) break
  }
  return out
}

/** entry_key → question, in the reader's locale. Read once per session by the
 *  shell, on the panel's first opening; a failed read is an empty map, which
 *  is an empty-state with no starters, never an error on the panel. */
let cached: { locale: string; map: Promise<Map<string, string>> } | null = null
export function loadHelpQuestions(locale: string | undefined): Promise<Map<string, string>> {
  const loc = locale ?? 'en-CA'
  if (cached && cached.locale === loc) return cached.map
  const map = (async () => {
    try {
      const { data, error } = await supabase.rpc('help_corpus', { p_locale: loc, p_lane: null })
      if (error) throw error
      const m = new Map<string, string>()
      for (const r of (data ?? []) as Array<{ entry_key: string; question: string }>) {
        if (r.entry_key && r.question) m.set(r.entry_key, r.question)
      }
      return m
    } catch {
      cached = null
      return new Map<string, string>()
    }
  })()
  cached = { locale: loc, map }
  return map
}
